import axios from "axios";
import { flexxState } from "./state";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEBOUNCE_MS = 1500;

// Build an authed axios on demand so we always read the latest token
function authHeaders() {
  const token = localStorage.getItem("flexx_access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

let pushTimer = null;
let syncing = false;
let suppressEvents = false; // don't re-trigger push when we write snapshot after a pull
let initialPullDone = false; // block pushes until first pull completes

// Test-only escape hatch — checked at every push and at initFlexxSync() entry.
// Either window.__flexxDisableCloudSync OR localStorage.flexx_disable_cloud_sync='1'
// will skip the network round-trip. localStorage variant survives reload (preferred
// for multi-page Playwright runs); the window variant is convenient inside a single page.
function isCloudSyncDisabled() {
  if (typeof window === "undefined") return false;
  if (window.__flexxDisableCloudSync) return true;
  try { return localStorage.getItem("flexx_disable_cloud_sync") === "1"; } catch (_) { return false; }
}

async function push() {
  if (syncing) return;
  if (!initialPullDone) return; // guard: never push before we've pulled
  if (isCloudSyncDisabled()) return; // test bypass
  syncing = true;
  try {
    const data = flexxState.snapshot();
    await axios.put(`${API}/flexx/state`, { data }, { headers: authHeaders(), withCredentials: true });
  } catch (err) {
    // Silent fail — local state still intact; will retry on next mutation
    console.warn("[flexxSync] push failed, will retry:", err?.message || err);
  } finally {
    syncing = false;
  }
}

function onMutation() {
  if (suppressEvents) return;
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(push, DEBOUNCE_MS);
}

let attached = false;

/**
 * Pull server state on login.
 *  - If server has data → overwrite localStorage with it.
 *  - If server is empty AND local has any meaningful data → seed server from local.
 * Then attach mutation listeners for debounced push.
 *
 * Test-only escape hatch: setting `window.__flexxDisableCloudSync = true` BEFORE
 * login (e.g. from a Playwright test) skips the pull/push entirely so localStorage
 * seeds win deterministically. Production code never sets this flag.
 */
export async function initFlexxSync() {
  // Attach listener once
  if (!attached) {
    window.addEventListener("flexx:mutation", onMutation);
    attached = true;
  }

  if (isCloudSyncDisabled()) {
    initialPullDone = true; // unblock future pushes if the flag flips off
    return { source: "disabled", data: null };
  }

  try {
    const res = await axios.get(`${API}/flexx/state`, { headers: authHeaders(), withCredentials: true });
    const serverData = res.data?.data;
    if (serverData && Object.keys(serverData).length > 0) {
      // Server is source of truth — overwrite local
      suppressEvents = true;
      flexxState.restore(serverData);
      suppressEvents = false;
      initialPullDone = true;
      window.dispatchEvent(new CustomEvent("flexx:restored", { detail: { source: "server" } }));
      return { source: "server", data: serverData };
    }
    // Server empty — if local has data, seed server
    initialPullDone = true;
    const local = flexxState.snapshot();
    const hasLocal = local.onboarded || local.xp > 0 || (local.journal || []).length > 0 || Object.keys(local.gates || {}).length > 0 || (local.courses && Object.values(local.courses).some(Boolean));
    if (hasLocal) {
      await push();
      return { source: "local-seeded", data: local };
    }
    return { source: "empty", data: null };
  } catch (err) {
    // Network / auth failure — allow local-only operation, don't block pushes forever
    initialPullDone = true;
    return { source: "offline", error: err?.message };
  }
}

/** Flush immediately (e.g., right before logout) */
export async function flushFlexxSync() {
  if (pushTimer) {
    clearTimeout(pushTimer);
    pushTimer = null;
  }
  await push();
}

/** Detach listener on logout and clear any pending push */
export function teardownFlexxSync() {
  if (attached) {
    window.removeEventListener("flexx:mutation", onMutation);
    attached = false;
  }
  if (pushTimer) {
    clearTimeout(pushTimer);
    pushTimer = null;
  }
  initialPullDone = false;
}

/** Clear localStorage flexx_* keys after logout so next user doesn't inherit state */
export function clearLocalFlexxState() {
  Object.keys(localStorage)
    .filter((k) => k.startsWith("flexx_") && k !== "flexx_access_token" && k !== "flexx_refresh_token" && k !== "flexx_disable_cloud_sync")
    .forEach((k) => localStorage.removeItem(k));
}
